import NotificationsItem from "./notifications-item";
import { NotificationsListProps } from "./notifications-type";
import { NotificationType } from "./notifications-type";
import { useBase64QueryState } from "../../utils/nuqs-custom-parsers/nuqs-custom-parsers";
import { FiltersType } from "./notifications-filters";
import { addDays, isAfter, isBefore } from "date-fns";
import { Skeleton } from "@repo/ui/components/ui/skeleton";
import { useTranslations } from "next-intl";
import { createContext, ReactNode, useState, useContext } from "react";

type NotificationListToggleContextType = {
  isToggled: boolean;
  setIsToggled: (value: boolean) => void;
};

const NotificationListToggleContext = createContext<
  NotificationListToggleContextType | undefined
>(undefined);

export const NotificationListToggleProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isToggled, setIsToggled] = useState(false);

  return (
    <NotificationListToggleContext.Provider value={{ isToggled, setIsToggled }}>
      {children}
    </NotificationListToggleContext.Provider>
  );
};

export const useNotificationListToggle = () => {
  return useContext(NotificationListToggleContext);
};

export default function NotificationsList({
  notifications,
  notificationsLoading,
  deleteNotification,
  markAsReadNotification,
  inline,
  customContent,
}: NotificationsListProps) {
  const t = useTranslations("Notifications");
  const [queryFilters] = useBase64QueryState<FiltersType>({}, {}, "f");

  const onDeleteNotification = (id: string) => {
    deleteNotification(id);
  };

  const filteredNotifications =
    notifications?.filter((notification: NotificationType) => {
      let matching = [
        !queryFilters.status ||
          queryFilters.status === "all" ||
          !notification.read,
      ];

      if (queryFilters.dateFrom && queryFilters.dateTo) {
        const start = new Date(queryFilters.dateFrom);
        const end = new Date(queryFilters.dateTo);

        matching.push(
          isAfter(notification.createdAt, start) &&
            isBefore(notification.createdAt, addDays(end, 1)),
        );
      }

      return inline || matching.every((val: boolean) => val);
    }) || [];

  return (
    <div className="flex flex-col items-center justify-center bg-transparent @[678px]:dark:bg-secondary-black @[678px]:bg-secondary-gray2  rounded-[20px] mt-10">
      {notificationsLoading ? (
        <Skeleton />
      ) : filteredNotifications && filteredNotifications.length > 0 ? (
        filteredNotifications.map((notification: NotificationType) => (
          <NotificationListToggleProvider key={notification.id}>
            <NotificationsItem
              deleteNotification={onDeleteNotification}
              markAsReadNotification={markAsReadNotification}
              notification={notification}
              customContent={customContent}
            />
          </NotificationListToggleProvider>
        ))
      ) : (
        <div className="h-40 flex items-center justify-center">
          <span className="text-lg font-semibold">{t("no-notifications")}</span>
        </div>
      )}
    </div>
  );
}
